import { useState, useEffect, useCallback } from 'react';
import { projects } from '@/mocks/projects';

const ProjectsReel = () => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const sorted = [...projects].sort((a, b) => parseInt(b.year) - parseInt(a.year));
  const total = sorted.length;

  const goNext = useCallback(() => {
    setDirection('next');
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setDirection('prev');
    setCurrent((prev) => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    if (isPaused || total === 0) return;
    const timer = setInterval(() => {
      goNext();
    }, 4500);
    return () => clearInterval(timer);
  }, [isPaused, total, goNext]);

  const active = sorted[current];
  if (!active) return null;

  return (
    <section className="py-20 bg-[#0C1D2E]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Recorrido visual</span>
          <h2 className="text-3xl md:text-4xl font-black text-white mt-2 mb-3">
            Nuestras Obras en Imágenes
          </h2>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            Desplázate por nuestras obras más destacadas construidas en todo Chile.
          </p>
        </div>

        {/* Reel container */}
        <div
          className="relative overflow-hidden rounded-xl select-none"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="relative h-[420px] md:h-[560px] w-full">
            {sorted.map((project, index) => {
              const isActive = index === current;
              let translateClass = 'translate-x-full opacity-0';
              if (isActive) translateClass = 'translate-x-0 opacity-100';
              else if (index === (current - 1 + total) % total) {
                translateClass = direction === 'next' ? '-translate-x-full opacity-0' : 'translate-x-full opacity-0';
              } else if (index === (current + 1) % total) {
                translateClass = direction === 'next' ? 'translate-x-full opacity-0' : '-translate-x-full opacity-0';
              }

              return (
                <div
                  key={project.id}
                  className={`absolute inset-0 w-full h-full transition-all duration-700 ease-in-out ${translateClass}`}
                >
                  <img
                    src={project.img}
                    alt={`${project.title} - IngemetalSur`}
                    className="w-full h-full object-contain md:object-cover bg-[#0C1D2E]"
                    loading="lazy"
                    style={{ filter: 'contrast(1.06) saturate(1.08)' }}
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C1D2E]/90 via-[#0C1D2E]/30 to-transparent" />

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="inline-block bg-[#C88A2A] text-white text-xs font-bold px-3 py-1 rounded-full">
                            {project.year}
                          </span>
                          <span className="inline-block bg-white/10 backdrop-blur-sm text-white/90 text-xs font-semibold px-3 py-1 rounded-full">
                            {project.category}
                          </span>
                        </div>
                        <h3 className="text-white text-xl md:text-3xl font-black leading-tight">
                          {project.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-white/70 text-sm">
                          <i className="ri-map-pin-line text-[#C88A2A]"></i>
                          <span>{project.location}</span>
                          <span className="mx-2 text-white/30">|</span>
                          <i className="ri-ruler-line text-[#C88A2A]"></i>
                          <span>{project.area}</span>
                        </div>
                      </div>
                      <div className="hidden md:flex items-center gap-3">
                        <button
                          onClick={goPrev}
                          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors cursor-pointer"
                          aria-label="Anterior"
                        >
                          <i className="ri-arrow-left-s-line text-xl"></i>
                        </button>
                        <button
                          onClick={goNext}
                          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-colors cursor-pointer"
                          aria-label="Siguiente"
                        >
                          <i className="ri-arrow-right-s-line text-xl"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile arrows */}
          <div className="flex md:hidden items-center justify-center gap-3 mt-4">
            <button
              onClick={goPrev}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white transition-colors cursor-pointer"
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={goNext}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white transition-colors cursor-pointer"
              aria-label="Siguiente"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-5">
            {sorted.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > current ? 'next' : 'prev');
                  setCurrent(index);
                }}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === current ? 'w-8 bg-[#4A7FB5]' : 'w-2 bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Ir a obra ${index + 1}`}
              />
            ))}
          </div>

          {/* Counter */}
          <div className="text-center mt-3">
            <span className="text-white/40 text-xs font-mono tracking-wider">
              {String(current + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsReel;